import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import ListaEmpresas from './components/ListaEmpresas';
import Reload from '../../components/reload/reload';
import Api from '../../services/Api';
import Grafico from './components/Grafico';
import Page from '../../components/Page/Page';
import Filter from './components/Filter';
import DataTable from './components/ListaMedidas/components/DataTable';
import DataQualityCard from './components/DataQualityCard';
import AlertModal from '../../components/AlertModal/AlertModal';
import Headers from '../../components/Headers/Headers';
import { useNavigate } from 'react-router-dom';
import dateFormat from 'dateformat';
import {
  buildEquipmentOptions,
  filterDataBySerials,
} from './utils/equipmentCompare';
import {
  EMPTY_CUSTOM_FILTER,
  readStoredRelatoriosFilters,
  persistRelatoriosFilters,
} from './utils/relatoriosFiltersStorage';
import '../DashMedidas/DashMedidas.css';
import '../../styles/SguUnitsPage.css';
import './Relatorios.css';

const Relatorios = () => {
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const { permissionSGU } = user;
  if (permissionSGU?.report?.read !== true) {
    navigate("/home");
  }

  const [empresasAll] = useState(
    JSON.parse(localStorage.getItem('units')) ||
    [JSON.parse(localStorage.getItem('cooperative'))] ||
    []
  );

  const initialFiltersRef = useRef(null);
  if (!initialFiltersRef.current) {
    initialFiltersRef.current = readStoredRelatoriosFilters(empresasAll);
  }
  const initialFilters = initialFiltersRef.current;

  const [equipmentList, setEquipmentList] = useState(initialFilters.equipmentList);
  const [curvaSelecionada, setCurvaSelecionada] = useState(initialFilters.curvaSelecionada);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [allData, setAllData] = useState([]);
  const [ultimoClicado, setUltimoClicado] = useState(initialFilters.ultimoClicado);
  const [customFilter, setCustomFilter] = useState({ ...initialFilters.customFilter });
  const [lastData, setLastData] = useState(initialFilters.lastData);
  const [filterData, setFilterData] = useState(initialFilters.filterData);

  const [hours, setHours] = useState({
    start: "",
    end: ""
  });

  const [isLoader, setIsLoader] = useState(false);

  const [empresas, setEmpresas] = useState(() => {
    const savedSearch = initialFilters.searchValue;
    if (!savedSearch) return empresasAll;
    return empresasAll.filter((item) =>
      (item?.name?.toUpperCase().includes(savedSearch.toUpperCase())) ||
      (item?.email?.toUpperCase().includes(savedSearch.toUpperCase())) ||
      (item?.cnpj?.toUpperCase().includes(savedSearch.toUpperCase()))
    );
  });
  const [searchValue, setSearchValue] = useState(initialFilters.searchValue);

  const [showModal, setShowModal] = useState(false);
  const [msg, setMsg] = useState({ title: "", message: "" });

  const lastResponseDataRef = useRef(null);
  const fetchRequestIdRef = useRef(0);
  /** Consulta ativa: 'default' | 'period' | 'date' */
  const activeQueryRef = useRef(initialFilters.activeQuery);
  const queryContextRef = useRef({
    startDate: initialFilters.startDate,
    endDate: initialFilters.endDate,
    ultimoClicado: initialFilters.ultimoClicado,
    lastData: initialFilters.lastData,
    filterData: initialFilters.filterData,
    customFilter: { ...initialFilters.customFilter },
  });
  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const getPersistedFilterSnapshot = (overrides = {}) => ({
    equipmentList,
    startDate,
    endDate,
    ultimoClicado,
    lastData,
    filterData,
    customFilter,
    activeQuery: activeQueryRef.current,
    searchValue,
    curvaSelecionada,
    ...overrides,
  });

  const commitActiveQuery = (query) => {
    activeQueryRef.current = query;
    persistRelatoriosFilters(getPersistedFilterSnapshot({ activeQuery: query }));
  };

  const getSelectedSerials = (list = equipmentList) =>
    new Set(
      (list || [])
        .map((mapping) => String(mapping.equipment || "").trim())
        .filter(Boolean)
    );

  const resolveNextCurva = (dataset, preferredCurva = "") => {
    if (preferredCurva && dataset.some((item) => item.grain === preferredCurva)) {
      return preferredCurva;
    }
    return dataset[dataset.length - 1]?.grain || "";
  };

  const applyKeptMeasurements = (kept) => {
    if (!kept.length) {
      lastResponseDataRef.current = [];
      setAllData([]);
      setData([]);
      setHours({ start: "", end: "" });
      return;
    }

    lastResponseDataRef.current = kept;
    setAllData(kept);

    setCurvaSelecionada((currentCurva) => {
      const nextCurva = resolveNextCurva(kept, currentCurva);

      const scoped = nextCurva
        ? kept.filter(({ grain }) => grain === nextCurva)
        : kept;
      setData(scoped);
      if (scoped.length > 0) {
        setHours({
          start: dateFormat(scoped[0].date, "UTC:dd/mm/yy' 'HH:MM"),
          end: dateFormat(scoped[scoped.length - 1].date, "UTC:dd/mm/yy' 'HH:MM"),
        });
      }
      return nextCurva;
    });
  };

  const keepRelevantMeasurements = (previousData, list = equipmentList) => {
    const kept = filterDataBySerials(previousData || [], getSelectedSerials(list));
    applyKeptMeasurements(kept);
  };

  const applyClientFilters = (rows, filter, advancedFilter = EMPTY_CUSTOM_FILTER) => {
    let filteredData = rows;

    if (filter === "input") {
      filteredData = filteredData.filter((i) => Number(i.humidity) > 14);
    } else if (filter === "output") {
      filteredData = filteredData.filter((i) => Number(i.humidity) < 13.5);
    }

    return filteredData.filter((item) =>
      Object.keys(advancedFilter || {}).every((key) => {
        if (advancedFilter[key] === "") return true;
        return item[key] && item[key] === advancedFilter[key];
      })
    );
  };

  const arraysEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const processData = (responseData) => {
    setData(responseData);

    if (responseData.length > 0) {
      const start = dateFormat(responseData[0].date, "UTC:dd/mm/yy' 'HH:MM");
      const end = dateFormat(responseData[responseData.length - 1].date, "UTC:dd/mm/yy' 'HH:MM");

      setHours({ start, end });
    }
  };

  const handleInitial = (
    responseData,
    list = equipmentList,
    { resetPeriod = false } = {}
  ) => {
    const newData = Array.isArray(responseData) ? responseData : [];

    if (newData.length) {
      const dataChanged = !arraysEqual(newData, lastResponseDataRef.current);
      if (dataChanged) {
        lastResponseDataRef.current = newData;
        setAllData(newData);
        setCurvaSelecionada((currentCurva) => {
          const nextCurva = resolveNextCurva(newData, currentCurva);
          processData(newData.filter(({ grain }) => grain === nextCurva));
          return nextCurva;
        });
      }
      if (resetPeriod && dataChanged) {
        setUltimoClicado(null);
        setStartDate("");
        setEndDate("");
      }
    } else {
      keepRelevantMeasurements(lastResponseDataRef.current || [], list);
    }
    setIsLoader(false);
  };

  const handleInitialData = async (list = equipmentList) => {
    const requestId = ++fetchRequestIdRef.current;
    const requestFilter = list;
    setIsLoader(true);

    try {
      const { data } = await Api.post(`/api/v1/measurements/company`, {
        filter: requestFilter,
      });
      if (requestId !== fetchRequestIdRef.current) return;
      commitActiveQuery("default");
      handleInitial(data, requestFilter, { resetPeriod: true });
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      setIsLoader(false);
    }
  };

  const handleChange = (value) => {
    setCurvaSelecionada(value);
    const filteredData = value ? allData.filter(i => i.grain === value) : allData;
    processData(filteredData);
  };

  queryContextRef.current = {
    startDate,
    endDate,
    ultimoClicado,
    lastData,
    filterData,
    customFilter,
  };

  useEffect(() => {
    persistRelatoriosFilters({
      equipmentList,
      startDate,
      endDate,
      ultimoClicado,
      lastData,
      filterData,
      customFilter,
      activeQuery: activeQueryRef.current,
      searchValue,
      curvaSelecionada,
    });
  }, [
    equipmentList,
    startDate,
    endDate,
    ultimoClicado,
    lastData,
    filterData,
    customFilter,
    searchValue,
    curvaSelecionada,
  ]);

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setFilterData("all");
    setUltimoClicado(null);
    setLastData(29);
    setCustomFilter({ ...EMPTY_CUSTOM_FILTER });
    lastResponseDataRef.current = null;
    activeQueryRef.current = "default";

    persistRelatoriosFilters(
      getPersistedFilterSnapshot({
        startDate: "",
        endDate: "",
        ultimoClicado: null,
        lastData: 29,
        filterData: "all",
        customFilter: { ...EMPTY_CUSTOM_FILTER },
        activeQuery: "default",
      })
    );

    if (equipmentList.length) {
      handleInitialData(equipmentList);
    }
  };

  const hasActiveFilters =
    Boolean(startDate) ||
    Boolean(endDate) ||
    ultimoClicado !== null ||
    filterData !== "all" ||
    Object.values(customFilter).some((value) => String(value).trim());

  const handleGetPeriod = async (
    param,
    ultimoClicadoIndex,
    filter,
    advancedFilter = customFilter,
    { list = equipmentList, silentEmpty = false, syncUi = true } = {}
  ) => {
    if (!list.length) {
      setMsg({ title: "Alerta", message: 'Escolha uma Unidade!' });
      handleShow();
      return;
    }

    const requestId = ++fetchRequestIdRef.current;
    setIsLoader(true);

    if (syncUi) {
      setLastData(param);
      setUltimoClicado(ultimoClicadoIndex);
      setFilterData(filter);
    }

    try {
      const { data } = await Api.post(`/measurements/period/filter`, {
        filter: list,
        param: param
      });
      if (requestId !== fetchRequestIdRef.current) return;

      if (data.length) {
        const filteredData = applyClientFilters(data, filter, advancedFilter);
        if (filteredData.length) {
          commitActiveQuery("period");
          handleInitial(filteredData, list);
          return;
        }
      }

      keepRelevantMeasurements(lastResponseDataRef.current || [], list);
      setIsLoader(false);
      if (!silentEmpty) {
        setMsg({ title: "Alerta", message: "Sem dados! Escolha um período maior!" });
        handleShow();
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      setIsLoader(false);
      if (!silentEmpty) {
        setMsg({ title: "Alerta", message: error?.response?.data?.status });
        handleShow();
      }
    }
  };

  const handledata = async ({
    list = equipmentList,
    silentEmpty = false,
    syncUi = true,
    start = startDate,
    end = endDate,
  } = {}) => {
    if (!list.length) {
      setMsg({ title: "Alerta", message: 'Escolha uma ou mais Unidades!' });
      handleShow();
      return;
    }

    const requestId = ++fetchRequestIdRef.current;
    setIsLoader(true);

    if (syncUi) {
      setFilterData("all");
      setUltimoClicado(null);
    }

    try {
      const { data } = await Api.post(`/measurements/period/date`, {
        filter: list,
        param: lastData,
        start,
        end
      });
      if (requestId !== fetchRequestIdRef.current) return;

      if (data.length) {
        commitActiveQuery("date");
        handleInitial(data, list);
        return;
      }

      keepRelevantMeasurements(lastResponseDataRef.current || [], list);
      setIsLoader(false);
      if (!silentEmpty) {
        setMsg({ title: "Alerta", message: "Sem dados! Escolha um período maior!" });
        handleShow();
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      setIsLoader(false);
      if (!silentEmpty) {
        setMsg({ title: "Alerta", message: error?.response?.data?.status });
        handleShow();
      }
    }
  };

  const refreshForEquipmentList = (list) => {
    const ctx = queryContextRef.current;
    const query = activeQueryRef.current;

    if (query === "date" && ctx.startDate && ctx.endDate) {
      handledata({
        list,
        silentEmpty: true,
        syncUi: false,
        start: ctx.startDate,
        end: ctx.endDate,
      });
      return;
    }

    if (query === "period") {
      handleGetPeriod(
        ctx.lastData,
        ctx.ultimoClicado,
        ctx.filterData,
        ctx.customFilter,
        { list, silentEmpty: true, syncUi: false }
      );
      return;
    }

    handleInitialData(list);
  };

  const handleSearchData = (value) => {
    setSearchValue(value);
    var result = [...empresasAll];
    let items = result && result.filter(item =>
      (item?.name?.toUpperCase().includes(value.toUpperCase())) ||
      (item?.email?.toUpperCase().includes(value.toUpperCase())) ||
      (item?.cnpj?.toUpperCase().includes(value.toUpperCase()))
    );
    setEmpresas(items);
  };

  const calculateAveragesByGrain = (dataList) => {
    if (dataList.length === 0) return [];

    const groupedByGrain = dataList.reduce((acc, item) => {
      const grain = item.grain;
      if (!acc[grain]) acc[grain] = [];
      acc[grain].push(item);
      return acc;
    }, {});

    const result = Object.entries(groupedByGrain).map(([grain, items]) => {
      const totalItems = items.length;

      const humiditySum = items.reduce((sum, item) => sum + parseFloat(item.humidity), 0);
      const temperatureSum = items.reduce((sum, item) => sum + parseFloat(item.temperature), 0);
      const phSum = items.reduce((sum, item) => sum + parseFloat(item.ph), 0);

      const humidityAvg = (humiditySum / totalItems).toFixed(1);
      const temperatureAvg = (temperatureSum / totalItems).toFixed(1);
      const phAvg = (phSum / totalItems).toFixed(1);

      const latestDate = items[items.length - 1].date;

      return {
        grain: grain,
        humidity: parseFloat(humidityAvg),
        temperature: parseFloat(temperatureAvg),
        ph: parseFloat(phAvg),
        date: new Date(latestDate).toISOString().replace("T", " ").slice(0, 19),
        totalMeasures: totalItems
      };
    });
    return result;
  };

  useEffect(() => {
    if (equipmentList.length) {
      refreshForEquipmentList(equipmentList);
    } else {
      lastResponseDataRef.current = null;
      setData([]);
      setAllData([]);
      setHours({ start: "", end: "" });
      setIsLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentList]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [curvaSelecionada]);

  const equipmentOptions = useMemo(
    () => buildEquipmentOptions(equipmentList, empresasAll, allData),
    [equipmentList, empresasAll, allData]
  );

  const curveOptions = useMemo(() => {
    const grains = new Set();
    (allData || []).forEach((item) => {
      const grain = String(item?.grain || "").trim();
      if (grain) grains.add(grain);
    });
    return [...grains].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [allData]);

  const selectedUnitsCount = useMemo(() => {
    const ids = new Set(
      equipmentList.map((item) => String(item.companyId || "")).filter(Boolean)
    );
    return ids.size;
  }, [equipmentList]);

  const unitsHeaderRight = selectedUnitsCount ? (
    <span className="dash-section-card__header-meta">
      {selectedUnitsCount} selecionada{selectedUnitsCount !== 1 ? "s" : ""}
    </span>
  ) : null;

  return (
    <Page title={"Relatórios"}>
      <Headers page={"Relatórios"} />

      {isLoader && <Reload />}

      <AlertModal
        show={showModal}
        handleClose={handleClose}
        msg={msg}
      />

      <div className="dash-medidas-page dash-medidas-container relatorios-page sgu-units-page">
        <div className="dash-medidas-stack">
          <Row className="dash-medidas-grid g-3 sgu-units-main-row relatorios-main-row">
            <Col xs={12} md={4} lg={3} xl={3} className="sgu-units-col relatorios-units-col">
              <ListaEmpresas
                user={user}
                companies={empresas}
                equipmentList={equipmentList}
                setEquipmentList={setEquipmentList}
                headerRight={unitsHeaderRight}
                onSearch={handleSearchData}
                searchValue={searchValue}
                listRef={scrollContainerRef}
              />
            </Col>

            <Col xs={12} md={8} lg={9} xl={9} className="relatorios-content-col">
              <div className="dash-medidas-stack" ref={containerRef}>
                <Grafico
                  data={data}
                  hours={hours}
                  curvaSelecionada={curvaSelecionada}
                  curveOptions={curveOptions}
                  onCurvaChange={handleChange}
                  equipmentOptions={equipmentOptions}
                  hasEquipmentSelection={equipmentList.length > 0}
                  filterContent={
                    <Filter
                      embedded
                      user={user}
                      allData={allData}
                      handleGetPeriod={handleGetPeriod}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      ultimoClicado={ultimoClicado}
                      filterData={filterData}
                      lastData={lastData}
                      startDate={startDate}
                      endDate={endDate}
                      handledata={() => handledata()}
                      permissionSGU={permissionSGU}
                      handleClearFilters={handleClearFilters}
                      hasActiveFilters={hasActiveFilters}
                      customFilter={customFilter}
                      setCustomFilter={setCustomFilter}
                      equipmentOptions={equipmentOptions}
                      selectedUnitsCount={selectedUnitsCount}
                      hours={hours}
                      curvaSelecionada={curvaSelecionada}
                      onPdfError={(nextMsg) => {
                        setMsg(nextMsg);
                        handleShow();
                      }}
                    />
                  }
                />

                <DataQualityCard
                  allData={allData}
                  equipmentOptions={equipmentOptions}
                  hasEquipmentSelection={equipmentList.length > 0}
                />

                {allData?.length > 0 && (
                  <DataTable
                    user={user}
                    data={calculateAveragesByGrain(allData)}
                    handleChange={handleChange}
                    curvaSelecionada={curvaSelecionada}
                    allData={allData}
                    permissionSGU={permissionSGU}
                  />
                )}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Page>
  );
};
export default Relatorios;
